import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch bar preferences
export const fetchBarPreferences = createAsyncThunk(
  'barPreference/fetchBarPreferences',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching bar preferences with page:', page, 'and limit:', limit);
      const response = await api.barPreferences.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Bar preferences fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch bar preferences:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching bar preferences:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching bar preferences');
    }
  }
);

// Search bar preferences with keyword
export const searchBarPreferences = createAsyncThunk(
  'barPreference/searchBarPreferences',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching bar preferences with keyword:', keyword);
      const response = await api.barPreferences.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Bar preferences search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search bar preferences:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching bar preferences:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching bar preferences');
    }
  }
);

// Create bar preference
export const createBarPreference = createAsyncThunk(
  'barPreference/createBarPreference',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating bar preference with data:', formData);
      const response = await api.barPreferences.create(formData);
      if (response.data.status) {
        log.info('Bar preference created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create bar preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating bar preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create bar preference');
    }
  }
);

// Update bar preference
export const updateBarPreference = createAsyncThunk(
  'barPreference/updateBarPreference',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating bar preference with ID:', id, 'and data:', formData);
      const response = await api.barPreferences.update(id, formData);
      if (response.data.status) {
        log.info('Bar preference updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update bar preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating bar preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating bar preference');
    }
  }
);

// Delete bar preference
export const deleteBarPreference = createAsyncThunk(
  'barPreference/deleteBarPreference',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting bar preference with ID:', id);
      const response = await api.barPreferences.delete(id);
      if (response.data.status) {
        log.info('Bar preference deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete bar preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting bar preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting bar preference');
    }
  }
);

// Bulk delete bar preferences
export const bulkDeleteBarPreferences = createAsyncThunk(
  'barPreference/bulkDeleteBarPreferences',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.barPreferences.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting bar preferences');
    }
  }
);

// Bulk activate/deactivate bar preferences
export const bulkUpdateBarPreferenceStatus = createAsyncThunk(
  'barPreference/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.barPreferences.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating bar preference status');
    }
  }
);

// Fetch a single bar preference by ID
export const fetchBarPreferenceById = createAsyncThunk(
  'barPreference/fetchBarPreferenceById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching bar preference with ID:', id);
      const response = await api.barPreferences.getById(id);
      if (response.data.status) {
        log.info('Bar preference fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch bar preference:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching bar preference:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching bar preference');
    }
  }
);

export const updateBarPreferenceOrders = createAsyncThunk(
  'barPreference/updateOrders',
  async (barPreferences) => {
    try {
      const response = await api.barPreferences.updateOrders(barPreferences);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update bar preference orders';
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const barPreferenceSlice = createSlice({
  name: 'barPreference',
  initialState,
  reducers: {
    reorderBarPreferences: (state, action) => {
      const { oldIndex, newIndex } = action.payload;
      const [movedItem] = state.items.splice(oldIndex, 1);
      state.items.splice(newIndex, 0, movedItem);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch bar preferences
      .addCase(fetchBarPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBarPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.barPreferences;
        state.total = parseInt(action.payload.total);
        state.page = parseInt(action.payload.page);
        state.pages = parseInt(action.payload.pages);
        state.limit = parseInt(action.payload.limit);
      })
      .addCase(fetchBarPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search bar preferences
      .addCase(searchBarPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchBarPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.barPreferences || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
      })
      .addCase(searchBarPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create bar preference
      .addCase(createBarPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBarPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createBarPreference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update bar preference
      .addCase(updateBarPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBarPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateBarPreference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete bar preference
      .addCase(deleteBarPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBarPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteBarPreference.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete bar preferences
      .addCase(bulkDeleteBarPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteBarPreferences.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteBarPreferences.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateBarPreferenceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateBarPreferenceStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateBarPreferenceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update bar preference orders
      .addCase(updateBarPreferenceOrders.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBarPreferenceOrders.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateBarPreferenceOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default barPreferenceSlice.reducer; 